if (!customElements.get('klaviyo-newsletter-form')) {
  class KlaviyoNewsletterForm extends HTMLElement {
    constructor() {
      super();

      if (window.initLazyScript) {
        window.initLazyScript(this, this.init.bind(this), 300);
      } else {
        this.init();
      }
    }

    init() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = this.querySelector('form');
      this.emailInput = this.querySelector('input[type="email"]');
      this.submitButtons = this.querySelectorAll('button[type="submit"]');
      this.errorEl = this.querySelector('[data-form-error]');
      this.successEl = this.querySelector('[data-form-success]');
      this.liveRegion = this.querySelector('[data-live-region]');

      this.publicApiKey = (this.dataset.publicApiKey || '').trim();
      this.listId = (this.dataset.listId || '').trim();
      this.customSource = (this.dataset.customSource || 'Shopify Theme Newsletter').trim();
      this.successMessage = (this.dataset.successMessage || 'Thanks for subscribing.').trim();
      this.fallbackErrorMessage = (this.dataset.errorMessage || 'Sorry, something went wrong. Please try again.').trim();
      this.locale = this.dataset.locale || 'en';
      this.country = this.dataset.country || 'EU';

      if (!this.form || !this.emailInput) return;

      this.handleSubmitBound = this.handleSubmit.bind(this);
      this.form.addEventListener('submit', this.handleSubmitBound);
    }

    setLiveMessage(message) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = message || '';
    }

    setLoading(isLoading) {
      this.submitButtons.forEach((button) => {
        button.disabled = isLoading;
        button.classList.toggle('is-loading', isLoading);
      });
    }

    clearMessages() {
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }

      if (this.successEl) {
        this.successEl.hidden = true;
        this.successEl.textContent = '';
      }

      this.emailInput.removeAttribute('aria-invalid');
      this.emailInput.removeAttribute('aria-describedby');
      this.setLiveMessage('');
    }

    showError(message) {
      if (!this.errorEl) return;

      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      this.emailInput.setAttribute('aria-invalid', 'true');
      this.emailInput.setAttribute('aria-describedby', this.errorEl.id);
      this.setLiveMessage(message);
    }

    showSuccess(message) {
      if (!this.successEl) return;

      this.successEl.textContent = message;
      this.successEl.hidden = false;
      this.successEl.focus();
      this.setLiveMessage(message);
    }

    getPayload(email) {
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  locale: this.locale,
                  location: {
                    country: this.country
                  } 
                }
              }
            },
            custom_source: this.customSource
          }
        }
      };

      if (this.listId) {
        payload.data.relationships = {
          list: {
            data: {
              type: 'list',
              id: this.listId
            }
          }
        };
      }

      return payload;
    }

    async parseError(response) {
      try {
        const data = await response.json();

        if (data?.errors?.length) {
          return data.errors
            .map((item) => item.detail || item.title)
            .filter(Boolean)
            .join(' ');
        }

        if (typeof data?.message === 'string' && data.message.trim()) {
          return data.message.trim();
        }
      } catch (error) {
        // noop
      }

      return this.fallbackErrorMessage;
    }

    async subscribeWithKlaviyo(email) {
      if (!this.publicApiKey) {
        throw new Error('Klaviyo public API key is missing.');
      }

      console.log(JSON.stringify(this.getPayload(email)));

      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.publicApiKey)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            revision: '2026-01-15'
          },
          body: JSON.stringify(this.getPayload(email))
        }
      );

      if (!response.ok) {
        const message = await this.parseError(response);
        throw new Error(message);
      }

      console.log(response);

      return response;
    }

    async handleSubmit(event) {
      event.preventDefault();
      this.clearMessages();

      if (!this.emailInput.checkValidity()) {
        this.emailInput.reportValidity();
        this.showError(this.emailInput.validationMessage || this.fallbackErrorMessage);
        return;
      }

      const email = this.emailInput.value.trim();

      if (!email) {
        this.showError(this.fallbackErrorMessage);
        return;
      }

      this.setLoading(true);

      try {
        await this.subscribeWithKlaviyo(email);
        this.form.reset();
        this.showSuccess(this.successMessage);
      } catch (error) {
        this.showError(error.message || this.fallbackErrorMessage);
      } finally {
        this.setLoading(false);
      }
    }
  }

  customElements.define('klaviyo-newsletter-form', KlaviyoNewsletterForm);
}